import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Robot, ChatsCircle, Headset, ChartLineUp, Plus, ArrowRight,
  CheckCircle, Phone, ChatCircleDots, TrendUp, Sparkle, Lightning, Books,
  FilePdf, DownloadSimple, BookOpen,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

function Stat({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="group relative bg-white border border-zinc-200 rounded-xl p-5 overflow-hidden hover:border-zinc-300 transition-colors">
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-[0.07] group-hover:opacity-[0.12] transition-opacity" style={{ background: accent }} />
      <div className="relative flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</span>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
          <Icon size={18} weight="bold" />
        </div>
      </div>
      <div className="relative mt-3 font-display font-extrabold text-3xl tracking-tight">{value}</div>
      {sub && <div className="relative mt-1 text-sm text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
  }, []);

  const activity = stats?.activity?.length ? stats.activity : [{ date: "—", messages: 0 }];

  return (
    <div className="p-6 sm:p-8">
      {/* premium hero banner */}
      <div className="relative overflow-hidden rounded-2xl bg-[#0A0A0A] text-white p-7 sm:p-9">
        <div className="absolute inset-0 dark-grid-bg opacity-40" />
        <div className="absolute -top-24 -right-10 h-72 w-72 rounded-full bg-[#0033FF]/30 blur-[110px]" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold mb-4">
              <Sparkle size={13} weight="fill" className="text-[#5C7CFF]" /> Kommandocenter
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
              Välkommen tillbaka, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="mt-2 text-zinc-400 max-w-md">Designa, träna och lansera AI-agenter för chatt & röst — allt på ett ställe.</p>
          </div>
          <Link data-testid="overview-new-agent" to="/app/agents"
            className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] rounded-md px-5 py-2.5 font-semibold transition-transform hover:scale-[1.02] shadow-lg">
            <Plus size={18} weight="bold" /> Ny agent
          </Link>
        </div>
      </div>

      {/* stats */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-agents" icon={Robot} label="Totalt antal agenter" value={stats?.total_agents ?? "—"} sub={`${stats?.live_agents ?? 0} live nu`} accent="#0033FF" />
        <Stat testid="stat-conversations" icon={ChatsCircle} label="Konversationer" value={stats?.total_conversations ?? "—"} sub={`${stats?.total_messages ?? 0} meddelanden`} accent="#7C3AED" />
        <Stat testid="stat-resolution" icon={CheckCircle} label="Lösningsgrad" value={`${stats?.resolution_rate ?? 0}%`} sub="Utan mänsklig hjälp" accent="#10B981" />
        <Stat testid="stat-escalations" icon={Headset} label="Eskaleringar" value={stats?.escalations ?? "—"} sub="Överlämnade till människor" accent="#F97316" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* chart */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <ChartLineUp size={20} weight="bold" className="text-[#0033FF]" />
              <h3 className="font-display font-bold text-lg">Meddelandevolym</h3>
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-400"><TrendUp size={14} weight="bold" /> Senaste 7 dagarna</span>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={activity}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0033FF" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#0033FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e4e4e7", fontSize: 13 }} />
              <Area type="monotone" dataKey="messages" stroke="#0033FF" strokeWidth={2.5} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* agent mix */}
        <div className="bg-white border border-zinc-200 rounded-xl p-6">
          <h3 className="font-display font-bold text-lg mb-5">Agentfördelning</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-[#0033FF]/5 border border-[#0033FF]/10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><ChatCircleDots size={20} weight="bold" className="text-[#0033FF]" /></div>
                <span className="font-semibold">Chattagenter</span>
              </div>
              <span className="font-display font-extrabold text-2xl">{stats?.chat_agents ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-50 border border-zinc-200">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-zinc-900/5 flex items-center justify-center"><Phone size={20} weight="bold" className="text-zinc-700" /></div>
                <span className="font-semibold">Röstagenter</span>
              </div>
              <span className="font-display font-extrabold text-2xl">{stats?.voice_agents ?? 0}</span>
            </div>
          </div>
          <Link to="/app/agents" data-testid="overview-manage-agents"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-white border border-zinc-200 hover:bg-zinc-50 rounded-md px-4 py-2.5 font-semibold text-sm transition-colors">
            Hantera agenter <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>

      {/* recent agents / quick start */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-lg">Dina agenter</h3>
          <Link to="/app/agents" className="text-sm font-semibold text-[#0033FF] hover:underline">Visa alla</Link>
        </div>
        {agents.length === 0 ? (
          <div className="flex flex-col sm:flex-row items-center gap-5 rounded-xl bg-gradient-to-br from-[#0033FF]/5 to-transparent border border-[#0033FF]/10 p-6">
            <div className="h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center shrink-0"><Lightning size={24} weight="fill" className="text-[#0033FF]" /></div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-display font-bold text-lg">Lansera din första agent</div>
              <p className="text-sm text-zinc-500">Skapa en chatt- eller röstagent, träna den på din kunskap och gå live på minuter.</p>
            </div>
            <Link to="/app/agents" className="inline-flex items-center gap-2 bg-[#0033FF] text-white rounded-md px-5 py-2.5 font-semibold shrink-0"><Plus size={18} weight="bold" /> Skapa agent</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {agents.slice(0, 6).map((a) => (
              <button key={a.id} data-testid={`overview-agent-${a.id}`} onClick={() => navigate(`/app/agents/${a.id}`)}
                className="text-left flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-[#0033FF]/40 hover:bg-zinc-50 transition-colors">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center text-lg shrink-0" style={{ background: `${a.primary_color || "#0033FF"}1A` }}>
                  {a.persona_emoji || (a.type === "voice" ? "📞" : "💬")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{a.name}</div>
                  <div className="text-xs text-zinc-400 flex items-center gap-2">
                    <span className="capitalize">{a.type}</span>
                    <span className="flex items-center gap-0.5"><Books size={11} /> {a.kb_count ?? 0}</span>
                  </div>
                </div>
                <span className={`h-2 w-2 rounded-full shrink-0 ${a.status === "live" ? "bg-green-500" : "bg-zinc-300"}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* resources / downloads */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="overview-resources">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/manual-pdf`} target="_blank" rel="noopener noreferrer"
          data-testid="download-manual" className="group flex items-center gap-4 bg-white border border-zinc-200 rounded-xl p-5 hover:border-[#0033FF]/40 hover:bg-zinc-50 transition-colors">
          <div className="h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center shrink-0"><BookOpen size={24} weight="fill" className="text-[#0033FF]" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold">Användarmanual</div>
            <p className="text-sm text-zinc-500">Komplett steg-för-steg-guide till alla funktioner. PDF med bilder.</p>
          </div>
          <DownloadSimple size={20} weight="bold" className="text-zinc-400 group-hover:text-[#0033FF] shrink-0" />
        </a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/sales-pdf`} target="_blank" rel="noopener noreferrer"
          data-testid="download-sales" className="group flex items-center gap-4 bg-white border border-zinc-200 rounded-xl p-5 hover:border-[#0033FF]/40 hover:bg-zinc-50 transition-colors">
          <div className="h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center shrink-0"><FilePdf size={24} weight="fill" className="text-[#0033FF]" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold">Säljmaterial</div>
            <p className="text-sm text-zinc-500">Snygg broschyr över våra lösningar och fördelar — perfekt att dela.</p>
          </div>
          <DownloadSimple size={20} weight="bold" className="text-zinc-400 group-hover:text-[#0033FF] shrink-0" />
        </a>
      </div>
    </div>
  );
}
